'use strict'

import { Component, Vue } from 'vue-property-decorator';
import { AppData, CareGiver, INCOME_RANGE_TEXT_APP, INCOME_RANGE } from "@/models/app-data"
import { CommonForm } from "@/models/common-form"
import { AppService } from "@/service/app.service"
import { CommonFormService } from "@/service/common-form.service"
import Swal from 'sweetalert2';
import { EligibilityService } from '@/service/eligibility.service';
import { WorkflowService } from '@/service/workflow.service';
import { Workflow } from '@/models/workflow'
import { Address } from '@/models/address'
import moment from 'moment';
import { ZipCode } from '@/models/zipcode';
import { ZipCodeService } from "@/service/zipcode.service";
import { County } from '@/models/county';

export { County } from '@/models/county';

export const WORKFLOW_PREFERRED_HEAD_START = 'Head Start or Early Head Start'
export const WORKFLOW_PREFERRED_GREAT_START = 'the Great Start Readiness Program'
export const WORKFLOW_PREFERRED_HEAD_START_AND_GREAT_START = 'Head Start and Great Start'

@Component
export default class CommonMixin extends Vue {

    getAppData(): AppData {
        const appService = new AppService();
        const data = appService.getAppData();
        return data;
    }

    saveAppData(appData: AppData): void {
        const appService = new AppService();
        appService.saveAppData(appData);
    }


    async workflowToAppData(workflowId: string): Promise<AppData> {
        const appData = {} as AppData;
        appData.careGiver = {} as CareGiver;

        const workflowService = new WorkflowService();
        const eligibilityService = new EligibilityService();

        const workflow = await workflowService.get(workflowId)

        if (workflow != null) {
            appData.numberChildren = workflow.numberchildren;
            appData.numberOfChildrenApplying = workflow.numberchildren;
            appData.number4YearOlds = workflow.number4yearolds;
            appData.numberInHousehold = workflow.numberinhousehold;
            appData.careGiver.phoneNumber = this.formatPhoneNumber10Digits(workflow.phonenumber);
            appData.workflowId = workflowId;
            appData.eligibleIncome = await eligibilityService.getEligibilityIncome(workflowId);
            switch (workflow.incomerange) {
                case INCOME_RANGE_TEXT_APP.FPL_LVL_1_OR_LESS:
                    appData.fpl = INCOME_RANGE.FPL_LVL_1_OR_LESS;
                    break;
                case INCOME_RANGE_TEXT_APP.FPL_LVL_1_TO_LVL_3:
                    appData.fpl = INCOME_RANGE.FPL_LVL_1_TO_LVL_3;
                    break;
                case INCOME_RANGE_TEXT_APP.FPL_LVL_3_PLUS:
                    appData.fpl = INCOME_RANGE.FPL_LVL_3_PLUS;
                    break;
                default:
                    appData.fpl = INCOME_RANGE.FPL_NONE;
                    break;
            }
            appData.preferredProgram = workflow.preferredprogram;
            appData.language = workflow.language;

            if (workflow.address != null) {
                appData.zipCode = workflow.address.zipcode;

                // Update the county in the workflow record if it is not set
                if ( workflow.address.county == null || workflow.address.county == '' ) {
                    const zipcodeService = new ZipCodeService();
                    const zipCodeData: Array<ZipCode>  =  await zipcodeService.GetActiveZipCodeData();
                    workflow.address.county = this.zipCodeToCounty( workflow.address.zipcode, zipCodeData );
                    await workflowService.update(workflowId, workflow);
                }
                
                appData.careGiver.streetAddress = workflow.address.streetaddress;
                appData.careGiver.city = workflow.address.city
                appData.careGiver.state = workflow.address.state;
                appData.careGiver.latitude = workflow.address.latitude;
                appData.careGiver.longitude = workflow.address.longitude;
                appData.careGiver.county = workflow.address.county;
                appData.careGiver.zipCode = workflow.address.zipcode;
            }
        }
        return appData;
    }

    formatPhoneNumber10Digits(value: string): string {
        let phoneNumber = '';

        if (value != null) {
            // If this is a 12 characters that starts with +1
            // Then remove the +1. This is how the number will be
            // saved
            if (value.length == 12 && value.startsWith('+1')) {
                value = value.replace('+1', '');
            }

            // strip down to numbers only
            const numbersOnly = value.replace(/\D/g, "");
            if (numbersOnly.length == 10) {
                phoneNumber = numbersOnly;
            } else if (numbersOnly.length === 11 && numbersOnly.startsWith('1')) {
                phoneNumber = numbersOnly.substr(1)
            }
        }
        return phoneNumber;
    }

    formatPhoneNumberWithDashes(value: string | undefined): string {
        let phoneNumber = "";
        phoneNumber = value ?? '';
        const numbersOnly = phoneNumber.replace(/\D/g, "");
        if (numbersOnly.length == 10) {
            phoneNumber = numbersOnly
                .replace(/[^0-9()-]/g, "")
                .replace(/(\d{3})(\d{3})(\d{4})/, "$1-$2-$3");
        }
        return phoneNumber;
    }

    formatMMDDYYYY(value: string | undefined): string {
        if (value != null) {
            return moment(value).format('MM/DD/YYYY');
        }
        return ''
    }

    formatDateMMDDYYYY(value: Date | undefined): string {
        if (value != null) {
            return moment(value).format('MM/DD/YYYY');
        }
        return ''
    }


    deleteAppData(): void {
        const appService = new AppService();
        appService.deleteAppData();
    }

    showError(message: string): void {
        Swal.fire({
            title: 'Oops...',
            text: 'The application encountered an error and was unable to continue. Please try again.',
            footer: message
        })
    }

    async createCommonForm(commonForm: CommonForm): Promise<void> {
        const service = new CommonFormService();
        await service.create(commonForm);
    }

    async updateCommonForm(commonForm: CommonForm): Promise<void> {
        const service = new CommonFormService();
        await service.update(commonForm.workflowid, commonForm);
    }

    async startAndFinishCommonForm(commonForm: CommonForm): Promise<void> {
        const service = new CommonFormService();
        await service.startAndFinish(commonForm);
    }

    allChildren4AndOver(data: AppData): boolean {
        return (data.numberChildren > 0 && data.numberChildren == data.number4YearOlds);
    }

    allChildrenUnder4(data: AppData): boolean {
        return (data.numberChildren > 0 && data.number4YearOlds == 0);
    }

    childrenBothOverAndUnder4(data: AppData): boolean {
        return (data.numberChildren > 0 && data.numberChildren > data.number4YearOlds && data.number4YearOlds > 0);
    }


    clearGSRPQualifiersList2(appData: AppData): void {
        appData.disability = false;
        appData.expelled = false;
        appData.englishSecond = false;
        appData.noEnglishHome = false;
        appData.parentalLoss = false;
        appData.toxicExposure = false;
        appData.sexualAbuse = false;
        appData.neglect = false;
        appData.protectiveServices = false;
        appData.under20Birth = false;
        appData.highRiskNeighborhood = false;
        appData.siblingIssue = false;
        appData.gsrpQualifierList2None = false;
    }

    clearGSRPQualifiers(appData: AppData): void {
        appData.noHighSchoolGraduation = false;
        appData.illiterate = false;
        appData.noEnglishHousehold = false;
        appData.parentExperiencedAbuse = false;
        appData.gsrpQualifierNone = false;
    }

    clearHSQualifier(appData: AppData): void {
        appData.foster = false;
        appData.benefits = false;
        appData.homeless = false;
    }

    clearHSQualifierList2(appData: AppData): void {
        appData.specialNeeds = false;
    }

    clearHSQualifierList3(appData: AppData): void {
        appData.migrantFarmWork = false;
    }

    async postWorkflowEligible(workflowId: string, preferredProgram: string): Promise<boolean> {
        if (workflowId != null) {
            const eligibilityService = new EligibilityService();
            const workflowService = new WorkflowService();

            const workflow = {
                preferredprogram: preferredProgram
            } as Workflow;

            await workflowService.update(workflowId, workflow);
            await eligibilityService.postEligibility(workflowId, true);
            return Promise.resolve(true)
        } else {
            return Promise.resolve(false)
        }
    }

    async postWorkflowNotEligible(workflowId: string): Promise<boolean> {
        if (workflowId != null) {
            const eligibilityService = new EligibilityService();
            return await eligibilityService.postEligibility(workflowId, false);
        } else {
            return Promise.resolve(false)
        }
    }

    get RecaptchaSiteKey(): string {
        // If we are runing in Cypress, use the test google captcha key
        if ((window as any).Cypress != null) {
            return "6LeIxAcTAAAAAJcZVRqyHh71UMIEGNQ_MXjiZKhI";
        } else {
            return "6Ld4fKkZAAAAAGI3a67NK5MEg88Fa6HY58UvnDot";
        }
    }

    async putWorkflowStep(workflowId: string, step: string): Promise<boolean> {
        if (workflowId != null && step != null) {
            const workflowService = new WorkflowService();
            await workflowService.putWebStep(workflowId, step);
            return Promise.resolve(true)
        } else {
            return Promise.resolve(false)
        }
    }

    async publishWebCDCClickEvent(workflowId: string): Promise<boolean> {
        if (workflowId != null) {
            const workflowService = new WorkflowService();
            await workflowService.publishWebCDCClickEvent(workflowId);
            return Promise.resolve(true)
        } else {
            return Promise.resolve(false)
        }
    }

    async publishWebLearnMoreClickEvent(workflowId: string): Promise<boolean> {
        if (workflowId != null) {
            const workflowService = new WorkflowService();
            await workflowService.publishWebLearnMoreClickEvent(workflowId);
            return Promise.resolve(true)
        } else {
            return Promise.resolve(false)
        }
    }

    GooglePlaceToAddress(place: any): [Address, boolean] {
        let address = {} as Address
        let isComplete = false;

        if (place.geometry != null) {

            const lat = place.geometry.location.lat();
            const lon = place.geometry.location.lng();
            const components = place.address_components;

            let item = components.find((a: any) => a.types.includes("street_number"))
            const streetNumber = item?.short_name || "";

            item = components.find((a: any) => a.types.includes("route"))
            const route = item?.short_name || "";

            item = components.find((a: any) => a.types.includes("locality"))
            const city = item?.short_name || "";

            item = components.find((a: any) => a.types.includes("administrative_area_level_1"))
            const state = item?.short_name || "";

            item = components.find((a: any) => a.types.includes("postal_code"))
            const zip = item?.short_name || "";

            item = components.find((a: any) => a.types.includes("administrative_area_level_2"))
            const county = item?.short_name || "";

            address = {
                address: place.formatted_address,
                streetaddress: `${streetNumber} ${route}`,
                city: city,
                county: county,
                state: state,
                zipcode: zip,
                latitude: lat,
                longitude: lon
            }

            isComplete = streetNumber != "" && address.city != "" && address.state != "" && address.zipcode != "";
        }

        return [address, isComplete];
    }

    // This was added to post a message to the IFrame in wordpress that is hosting this sit.
    // It will enable us to work around a Safari only scrolling issue
    postNavigatedToParent() {
        window.parent.postMessage({ "data": "ece-navigated" }, "*",);
    }

    zipCodeToCounty(zipCode: string, zipCodeData: ZipCode[] | undefined): County {
        
        if (zipCodeData == null) {
            return County.Unknown;
        }

        const data = zipCodeData.find(z => z.zipcode == zipCode);

        if (data == null) {
            return County.Unknown;
        }

        const county = data.county.toUpperCase().trim();
        if (county == 'OAKLAND') {
            return County.Oakland;
        } else if (county == 'WAYNE') {
            return County.Wayne;
        } else if (county == 'WASHTENAW') {
            return County.Washtenaw;
        } else if (county == 'MACOMB') {
            return County.Macomb;
        } else {
            return County.Unknown;
        }
    }

    findZipCodeData(zipCode: string, zipCodeData: ZipCode[] | undefined): ZipCode | undefined {
        if (zipCodeData == null) {
            return undefined;
        }

        const data = zipCodeData.find(z => z.zipcode == zipCode);
        return data;
    }


    isValidCounty( countyName: string ): boolean {
        if ( countyName == null ) {
            return false;
        }

        return countyName.includes(County.Wayne) 
            || countyName.includes(County.Oakland)
            || countyName.includes(County.Macomb)
            || countyName.includes(County.Washtenaw)
  }

  toValidCounty( countyName: string ): string {
        if ( countyName == null) {
            return '';
        }
        else if (countyName.includes(County.Wayne)) {
            return County.Wayne;
        } else if (countyName.includes(County.Oakland)) {
            return County.Oakland;
        }
        else if (countyName.includes(County.Macomb)) {
            return County.Macomb;
        }
        else if (countyName.includes(County.Washtenaw)) {
            return County.Washtenaw;
        }
        else {
            return County.Unknown;
        }
  }
}

